import {
	AccountOverviewAdAccountsResponseDTO,
	AccountOverviewWebsitesResponseDTO,
	AccountOverviewWorkspacesResponseDTO
} from 'api-models'
import { ApiClient } from 'services/api-client'

/**
 * DEV-2339 · Account Overview API client.
 * Thin wrappers over the `/account-overview/*` backend namespace (T2/T4/T11).
 */

export type AccountOverviewVisibility = {
	visible: boolean
}

/**
 * Gates the header monitor icon + the page. True only when the user can access a
 * workspace that has ≥1 website AND ≥1 connected ad account (computed server-side).
 */
const getVisibility = async (): Promise<AccountOverviewVisibility> => {
	const { data } = await ApiClient.client.get('/account-overview/visibility')
	return data.data
}

/**
 * Marks the one-time discovery coachmark as seen for the current user. Fired when
 * the page is opened by any route (and on coachmark dismissal — T12). Idempotent.
 */
const markIntroSeen = async (): Promise<void> => {
	await ApiClient.client.post('/account-overview/intro-seen')
}

export type WorkspacesCard = 'all' | 'active' | 'usage_exceeding' | 'pending_issues'

export type WorkspacesQueryParams = {
	search?: string
	statuses?: string[]
	plans?: string[]
	card?: WorkspacesCard
	sortBy?: 'name' | 'status' | 'usage' | 'reset'
	sortOrder?: 1 | -1
	limit?: number
	skip?: number
}

/** Cross-workspace Workspaces table (T4). Server-side search/filter/sort/pagination. */
const getWorkspaces = async (
	params: WorkspacesQueryParams
): Promise<AccountOverviewWorkspacesResponseDTO> => {
	const qs = new URLSearchParams()
	if (params.search) qs.set('search', params.search)
	if (params.statuses?.length) qs.set('statuses', params.statuses.join(','))
	if (params.plans?.length) qs.set('plans', params.plans.join(','))
	if (params.card && params.card !== 'all') qs.set('card', params.card)
	if (params.sortBy) qs.set('sortBy', params.sortBy)
	if (params.sortOrder) qs.set('sortOrder', String(params.sortOrder))
	qs.set('limit', String(params.limit ?? 10))
	qs.set('skip', String(params.skip ?? 0))

	const { data } = await ApiClient.client.get(`/account-overview/workspaces?${qs.toString()}`)
	return data.data
}

export type WebsitesCard = 'all' | 'pending_issues' | 'ivt_24h' | 'ivt_7d'

export type WebsitesQueryParams = {
	search?: string
	/** Ad-platform filter — GADS | FADS | BADS. */
	platforms?: string[]
	/** Connection-status filter — ACTIVE | NO_ACCESS. */
	connectionStatuses?: string[]
	/** Protection-status filter — the 5 badges. */
	protectionStatuses?: string[]
	card?: WebsitesCard
	sortBy?: 'name' | 'protection' | 'ivt_24h' | 'ivt_7d' | 'last_blocked'
	sortOrder?: 1 | -1
	limit?: number
	skip?: number
}

/** Cross-workspace Websites table (T9). Server-side search/filter/sort/pagination. */
const getWebsites = async (
	params: WebsitesQueryParams
): Promise<AccountOverviewWebsitesResponseDTO> => {
	const qs = new URLSearchParams()
	if (params.search) qs.set('search', params.search)
	if (params.platforms?.length) qs.set('platforms', params.platforms.join(','))
	if (params.connectionStatuses?.length) qs.set('connectionStatuses', params.connectionStatuses.join(','))
	if (params.protectionStatuses?.length) qs.set('protectionStatuses', params.protectionStatuses.join(','))
	if (params.card && params.card !== 'all') qs.set('card', params.card)
	if (params.sortBy) qs.set('sortBy', params.sortBy)
	if (params.sortOrder) qs.set('sortOrder', String(params.sortOrder))
	qs.set('limit', String(params.limit ?? 10))
	qs.set('skip', String(params.skip ?? 0))

	const { data } = await ApiClient.client.get(`/account-overview/websites?${qs.toString()}`)
	return data.data
}

/** Nested ad-accounts for one website (T9). Lazy-loaded on row expand. */
const getWebsiteAdAccounts = async (
	assetId: string,
	params: { limit?: number; skip?: number } = {}
): Promise<AccountOverviewAdAccountsResponseDTO> => {
	const qs = new URLSearchParams()
	qs.set('limit', String(params.limit ?? 5))
	qs.set('skip', String(params.skip ?? 0))
	const { data } = await ApiClient.client.get(
		`/account-overview/websites/${assetId}/ad-accounts?${qs.toString()}`
	)
	return data.data
}

export const accountOverviewApi = {
	getVisibility,
	markIntroSeen,
	getWorkspaces,
	getWebsites,
	getWebsiteAdAccounts
}
